import { useGetTermAndConditionsQuery } from "@/services/authAPI";
import {
  ActionIcon,
  Card,
  Group,
  Modal,
  ScrollArea,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  opened: boolean;
  close: () => void;
}

const TermCondition = ({ opened, close }: Props) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: termData } = useGetTermAndConditionsQuery();
  const result = get(termData, "body.termAndConditions", []);
  const isEnglish = (i18n.language || "vi").startsWith("en");

  /**
   * Tên điều khoản: backend đã trả sẵn bản tiếng Anh ở `nameEN`, trước đây màn này
   * bỏ qua nên luôn hiện tiếng Việt. Thiếu bản EN thì lùi về `name`.
   */
  const pickTermName = (item: { name?: string; nameEN?: string }): string =>
    (isEnglish && item?.nameEN) || item?.name || "";

  const handleDownload = (item: any) => {
    const link = document.createElement("a");
    // File cũng có bản tiếng Anh riêng khi quản trị đã tải lên.
    link.href = (isEnglish && item?.fileEN) || item?.file;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="50%"
      centered
      withCloseButton={false}
      title={<Text fw={600}>{t("TERMS_CONDITIONS_LIST")}</Text>}
    >
      <Card p="0">
        {/* Ô tìm kiếm */}
        <TextInput
          placeholder={t("TEN_IEU_KHOAN_IEU_KIEN")}
          icon={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          mb="md"
        />

        {/* Tabs */}
        <Tabs defaultValue="the">
          <Tabs.List>
            <Tabs.Tab value="the">{t("CARDS_TAB")}</Tabs.Tab>
            <Tabs.Tab value="ngan-hang-so">{t("DIGITAL_BANKING_TAB")}</Tabs.Tab>
            <Tabs.Tab value="tai-khoan">{t("ACCOUNT_LABEL")}</Tabs.Tab>
            <Tabs.Tab value="tiet-kiem">{t("SAVINGS_TAB")}</Tabs.Tab>
            <Tabs.Tab value="tien-vay">{t("LOANS_TAB")}</Tabs.Tab>
          </Tabs.List>

          {/* Nội dung Tab “Thẻ” */}
          <Tabs.Panel value="the" pt="md">
            <ScrollArea h={250}>
              <Table verticalSpacing="sm" highlightOnHover>
                <thead>
                  <tr>
                    <th>{t("TERMS_CONDITIONS_NAME")}</th>
                    <th>{t("EFFECTIVE_DATE")}</th>
                    <th>{t("DOWNLOAD_FILE")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.map((item, idx) => (
                    <tr key={item.name}>
                      <td>{pickTermName(item)}</td>
                      <td>
                        <Text fw={600}>{item.effectDate}</Text>
                      </td>
                      <td>
                        <Group spacing={6} onClick={() => handleDownload(item)}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            component="a"
                            href="#"
                            title={t("XEM_CHI_TIET")}
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                          <Text
                            size="sm"
                            c="blue"
                            style={{ cursor: "pointer" }}
                          >
                            {t("VIEW_DETAILS")}
                          </Text>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>

          {/* Các tab khác (chưa có dữ liệu) */}
          <Tabs.Panel value="ngan-hang-so" pt="md">
            <Text size="sm" c="dimmed">
              {t("NO_DATA_YET")}
            </Text>
          </Tabs.Panel>
          <Tabs.Panel value="tai-khoan" pt="md">
            <Text size="sm" c="dimmed">
              {t("NO_DATA_YET")}
            </Text>
          </Tabs.Panel>
          <Tabs.Panel value="tiet-kiem" pt="md">
            <Text size="sm" c="dimmed">
              {t("NO_DATA_YET")}
            </Text>
          </Tabs.Panel>
          <Tabs.Panel value="tien-vay" pt="md">
            <Text size="sm" c="dimmed">
              {t("NO_DATA_YET")}
            </Text>
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Modal>
  );
};

export default TermCondition;
